import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router";
import { ColumnDef } from "@tanstack/react-table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPencil, faTrashCan } from "@fortawesome/pro-solid-svg-icons";
import DataTable from "@components/DataTable";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import ScopeStackModal from "@components/ScopeStackModal/ScopeStackModal";
import SmallSpinner from "@components/SmallSpinner/SmallSpinner";
import { handleAlert } from "@utils/helperFunctions";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import { AdminListCard } from "@components/CatalogIndex/AdminListCard";
import { useV2DeleteProjectContactMutation } from "@generated";
import useListContacts from "../api/useListContacts";
import { Contact, CONTACT_TYPE_LABELS, ContactType } from "../types/Contact";
import useStyles from "../styles/useStyles";

interface Props {
  projectId: string;
  projectStatus: string;
  permission: string;
}

const Show: React.FC<Props> = ({ projectId, projectStatus, permission }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const styles = useStyles();

  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const [errorMessages, setErrorMessages] = useState("");
  const [showFailAlert, setShowFailAlert] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);

  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [deleteQueue, setDeleteQueue] = useState<string[]>([]);
  const [isDeleting, setIsDeleting] = useState(false);

  const [deleteContact] = useV2DeleteProjectContactMutation();

  const { contacts, contactsLoading, contactsFetching, pageMeta, refetch } =
    useListContacts({
      projectId,
      page: { number: pageNumber, size: pageSize },
    });

  useEffect(() => {
    window.history.replaceState({}, "");
    refetch();
    if (location?.state?.edit) {
      setSuccessMessage("Contact updated successfully!");
      setShowSuccessAlert(true);
    }
    if (location?.state?.new) {
      setSuccessMessage("Contact created successfully!");
      setShowSuccessAlert(true);
    }
  }, [pageNumber]);

  const handleDelete = () => {
    setDeleteQueue([...selectedIds]);
    setShowConfirmationModal(true);
  };

  const confirmDelete = () => {
    setIsDeleting(true);
    if (deleteQueue.length === 0) return;

    const promises = deleteQueue.map((id) => deleteContact({ id }));

    Promise.all(promises)
      .then(() => {
        setSuccessMessage(
          `Contact${deleteQueue.length > 1 ? "s" : ""} deleted successfully!`
        );
        setShowSuccessAlert(true);
        setIsDeleting(false);
        setShowConfirmationModal(false);
        setSelectedIds([]);
        setDeleteQueue([]);
        refetch();
      })
      .catch(() => {
        setErrorMessages(
          "One or more contacts could not be deleted. Please try again later."
        );
        setShowFailAlert(true);
        setIsDeleting(false);
      });
  };

  const getConfirmationMessage = () => (
    <div style={{ color: "#1C2655", fontSize: "14px" }}>
      {isDeleting ? (
        <SmallSpinner />
      ) : (
        <span style={{ fontWeight: "700" }}>
          {`You are about to delete ${selectedIds.length} contact${
            selectedIds.length > 1 ? "s" : ""
          }.`}
        </span>
      )}
      <ul style={{ marginTop: "10px" }}>
        {selectedIds.map((id) => {
          const contactName = contacts.find((c) => c.id === id)?.name;
          return <li key={id}>{contactName}</li>;
        })}
      </ul>
      This action cannot be undone. Do you wish to proceed?
    </div>
  );

  const formatContactType = (type: ContactType): string => {
    return CONTACT_TYPE_LABELS[type] || type;
  };

  const columns: ColumnDef<Contact>[] = [
    {
      header: "Contact",
      cell(props) {
        const contact = props.row.original;
        if (contact.id === "0") {
          return (
            <div
              className={styles.editLink}
              onClick={() => {
                navigate(`/projects/${projectId}/project_contacts/new`, {
                  state: {},
                });
              }}
            >
              {contact.name}
            </div>
          );
        }
        return (
          <div
            className={styles.editLink}
            onClick={() => {
              navigate(
                `/projects/${projectId}/project_contacts/edit/${contact.id}`,
                { state: { contact } }
              );
            }}
          >
            {contact.name} <FontAwesomeIcon icon={faPencil} />
          </div>
        );
      },
    },
    {
      header: "Title",
      cell(props) {
        return <span>{props.row.original.title}</span>;
      },
    },
    {
      header: "Email",
      cell(props) {
        return <span>{props.row.original.email}</span>;
      },
    },
    {
      header: "Phone",
      cell(props) {
        return <span>{props.row.original.phone}</span>;
      },
    },
    {
      header: "Contact Type",
      cell(props) {
        const contact = props.row.original;
        if (contact.id === "0") return null;
        return <span>{formatContactType(contact.contactType)}</span>;
      },
    },
  ];

  if (permission === "none") {
    return (
      <div className={styles.page}>
        You do not have permission to view this page. Please contact your
        administrator for assistance.
      </div>
    );
  }

  return (
    <div className={styles.page}>
      {handleAlert(
        showFailAlert,
        errorMessages,
        setShowFailAlert,
        "warning",
        ToastAlert
      )}
      {handleAlert(
        showSuccessAlert,
        successMessage,
        setShowSuccessAlert,
        "success",
        ToastAlert
      )}
      <AdminListCard
        title="Project Contacts"
        onNew={
          projectStatus === "building"
            ? () =>
                navigate(`/projects/${projectId}/project_contacts/new`, {
                  state: {},
                })
            : undefined
        }
        newButtonLabel="Add Contact"
        batchActions={[
          {
            icon: faTrashCan,
            label: "Delete",
            onClick: handleDelete,
            variant: "danger",
          },
        ]}
        selectedCount={selectedIds.length}
      >
        {contactsLoading || contactsFetching ? (
          <ScopeStackSpinner />
        ) : (
          <DataTable
            data={
              contacts.length > 0
                ? contacts
                : [
                    {
                      id: "0",
                      name: "Add contacts to get started",
                      title: "",
                      email: "",
                      phone: "",
                      contactType: "customer_contact" as ContactType,
                      projectVariables: [],
                    },
                  ]
            }
            columns={columns}
            selectable={
              contacts.length > 0 &&
              permission !== "view" &&
              projectStatus === "building"
            }
            striped
            hover
            totalPages={pageMeta.pageCount}
            totalRows={pageMeta.recordCount}
            currentPage={pageNumber}
            setCurrentPage={setPageNumber}
            pageSize={pageSize}
            onPageSizeChange={setPageSize}
            paginationEnabled
            pageSizeEnabled
            selectedIds={selectedIds}
            setSelectedIds={setSelectedIds}
          />
        )}
        <ScopeStackModal
          modalTitle="Delete Contact"
          modalBody={getConfirmationMessage()}
          handleButton2Click={isDeleting ? () => null : confirmDelete}
          handleClose={() => {
            setShowConfirmationModal(false);
            setDeleteQueue([]);
          }}
          show={showConfirmationModal}
          handleButton1Click={() => {
            setShowConfirmationModal(false);
            setDeleteQueue([]);
          }}
          deleteModal={true}
          button1Text="Cancel"
          button2Text="Delete"
        />
      </AdminListCard>
    </div>
  );
};

export default Show;
